/**
 * The Tasks app overview surface. Hero
 * figure + stat tiles, streak card beside the year heatmap, per-list
 * breakdown, achievements, the forever completed log, and API keys. All data
 * is REST (projection-backed); the live docs never hold history.
 */
import { useEffect, useMemo, useState } from "react";
import { Circle, CircleCheck, Flame, Trophy } from "lucide-react";
import { ACHIEVEMENTS } from "@hydra-notes/shared";
import { useTaskStats, scheduleAchievementCheck } from "@/data/taskStats";
import { toggleTaskDone, useAllTasks, type AllTaskRow } from "@/data/taskHooks";
import { useSharedFolders } from "@/data/sharedFolders";
import { useSessionDone } from "@/store/sessionDone";
import { useAuth } from "@/data/auth";
import { openItem } from "@/commands/nav";
import { focusTask } from "@/commands/tasks";
import { dueLabel } from "@hydra-notes/shared";
import { Button } from "@/ui/Button";
import { Heatmap } from "./Heatmap";
import { ApiKeysSection } from "./ApiKeysSection";
import { DiscordSection } from "@/discord/DiscordSection";
import { celebrateComplete } from "./celebrate";
import s from "./dashboard.module.css";

function compact(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 10_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}

export function TasksDashboard() {
  const signedIn = useAuth((a) => a.status === "signedIn");
  const stats = useTaskStats((st) => st.stats);
  const achievements = useTaskStats((st) => st.achievements);
  const history = useTaskStats((st) => st.history);
  const historyCursor = useTaskStats((st) => st.historyCursor);
  const historyLoading = useTaskStats((st) => st.historyLoading);

  useEffect(() => {
    if (!signedIn) return;
    const st = useTaskStats.getState();
    void st.refreshStats();
    void st.refreshAchievements();
    void st.loadHistory({ reset: true });
    void st.refreshKeys();
  }, [signedIn]);

  if (!signedIn) {
    return (
      <div className={s.view}>
        <div className={s.inner}>
          <CurrentTasks />
          <div className={s.signedOut}>
            Stats, history and the task API need a cloud account. Your task lists themselves work offline either way.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={s.view}>
      <div className={s.inner}>
        <CurrentTasks />
        <header className={s.heroRow}>
          <div className={s.hero}>
            <span className={s.heroValue}>{stats ? compact(stats.completedTotal) : "–"}</span>
            <span className={s.heroLabel}>tasks completed, ever</span>
          </div>
          <div className={s.tiles}>
            <StatTile label="Today" value={stats?.completedToday} />
            <StatTile label="This week" value={stats?.completedWeek} />
            <StatTile label="Created" value={stats?.createdTotal} />
            <StatTile label="Best day" value={stats?.bestDay} />
          </div>
        </header>

        <section className={s.streakRow}>
          <div className={s.streakCard}>
            <Flame size={26} className={stats && stats.currentStreak > 0 ? s.flameLit : s.flame} />
            <span className={s.streakValue}>{stats?.currentStreak ?? 0}</span>
            <span className={s.streakLabel}>day streak</span>
            <span className={s.streakBest}>Best: {stats?.bestStreak ?? 0} days</span>
          </div>
          <div className={s.heatCard}>
            <Heatmap data={stats?.heatmap ?? []} />
          </div>
        </section>

        {stats && stats.perList.length > 0 ? (
          <section className={s.section}>
            <h2 className={s.sectionTitle}>Lists</h2>
            <div className={s.listBars}>
              {stats.perList
                .slice()
                .sort((a, b) => b.open + b.completed - (a.open + a.completed))
                .map((l) => {
                  const total = l.open + l.completed;
                  const pct = total ? Math.round((l.completed / total) * 100) : 0;
                  return (
                    <button key={`${l.spaceId}/${l.listId}`} type="button" className={s.listBar} onClick={() => openItem(l.listId)}>
                      <span className={s.listBarTitle}>{l.title}</span>
                      <span className={s.listBarTrack}>
                        <span className={s.listBarFill} style={{ width: `${pct}%` }} />
                      </span>
                      <span className={s.listBarMeta}>
                        {l.open} open · {l.completed} done
                      </span>
                    </button>
                  );
                })}
            </div>
          </section>
        ) : null}

        <section className={s.section}>
          <h2 className={s.sectionTitle}>
            <Trophy size={15} /> Achievements
            <span className={s.sectionCount}>
              {achievements.filter((a) => a.unlockedAt).length}/{ACHIEVEMENTS.length}
            </span>
          </h2>
          <div className={s.achGrid}>
            {(achievements.length ? achievements : ACHIEVEMENTS.map((a) => ({ ...a, unlockedAt: null, progress: 0 }))).map(
              (a) => (
                <div key={a.key} className={[s.ach, a.unlockedAt ? s.achUnlocked : ""].join(" ")}>
                  <Trophy size={16} className={s.achIcon} />
                  <span className={s.achTitle}>{a.title}</span>
                  <span className={s.achDesc}>{a.description}</span>
                  {!a.unlockedAt ? (
                    <span className={s.achBarTrack}>
                      <span className={s.achBarFill} style={{ width: `${Math.round((a.progress / a.threshold) * 100)}%` }} />
                    </span>
                  ) : (
                    <span className={s.achDate}>{new Date(a.unlockedAt).toLocaleDateString()}</span>
                  )}
                </div>
              ),
            )}
          </div>
        </section>

        <section className={s.section}>
          <h2 className={s.sectionTitle}>Completed</h2>
          {history.length === 0 ? (
            <p className={s.mutedText}>Completed tasks land here, forever. Go check something off.</p>
          ) : (
            <div className={s.historyRows}>
              {history.map((h) => (
                <div key={`${h.seq}`} className={s.historyRow}>
                  <span className={s.historySeq}>#{h.seq}</span>
                  <span className={s.historyTitle}>{h.title || "Untitled task"}</span>
                  <span className={s.historyList}>{h.listTitle}</span>
                  <span className={s.historyDate}>{new Date(h.doneAt).toLocaleString()}</span>
                </div>
              ))}
              {historyCursor ? (
                <Button variant="ghost" size="sm" disabled={historyLoading} onClick={() => void useTaskStats.getState().loadHistory()}>
                  {historyLoading ? "Loading..." : "Load more"}
                </Button>
              ) : null}
            </div>
          )}
        </section>

        <DiscordSection />
        <ApiKeysSection />

        {stats ? (
          <footer className={s.footer}>
            Task number <span className={s.footerSeq}>#{stats.platformTaskCounter.toLocaleString()}</span> was the
            latest minted across all of Hydra Notes.
          </footer>
        ) : null}
      </div>
    </div>
  );
}

const CURRENT_PAGE = 25;

/**
 * The Vikunja-home style overview leading the dashboard: every open task
 * across every list, newest first, checkable in place. Live-doc sourced so
 * it works offline and updates in real time.
 */
function CurrentTasks() {
  const { rows, loaded } = useAllTasks();
  const folders = useSharedFolders((st) => st.folders);
  const userId = useAuth((st) => st.user?.id) ?? "local";
  const [shown, setShown] = useState(CURRENT_PAGE);

  const viewerSpaces = useMemo(
    () => new Set(folders.filter((f) => f.role === "viewer" || f.locked).map((f) => f.id)),
    [folders],
  );
  const open = useMemo(
    () => rows.filter((r) => !r.task.done).sort((a, b) => b.task.createdAt - a.task.createdAt),
    [rows],
  );

  const toggle = (row: AllTaskRow, el: Element | null) => {
    if (viewerSpaces.has(row.list.spaceId)) return;
    if (!toggleTaskDone(row.list.spaceId, row.list.listId, row.task.id, true, userId)) return;
    useSessionDone.getState().add(row.list.listId, row.task.id);
    celebrateComplete(el);
    scheduleAchievementCheck();
  };

  return (
    <section className={s.section}>
      <h2 className={s.sectionTitle}>
        Current tasks
        {loaded ? <span className={s.sectionCount}>{open.length} open</span> : null}
      </h2>
      {loaded && open.length === 0 ? (
        <p className={s.mutedText}>Nothing open anywhere. Enjoy it.</p>
      ) : (
        <div className={s.currentGrid}>
          {open.slice(0, shown).map((row) => (
            <div key={`${row.list.listId}/${row.task.id}`} className={s.currentRow}>
              <button
                type="button"
                className={s.currentCheck}
                aria-label={`Mark "${row.task.title}" done`}
                disabled={viewerSpaces.has(row.list.spaceId)}
                onClick={(e) => toggle(row, e.currentTarget)}
              >
                {row.task.done ? <CircleCheck size={15} /> : <Circle size={15} />}
              </button>
              <button
                type="button"
                className={s.currentBody}
                onClick={() => {
                  openItem(row.list.listId);
                  focusTask(row.task.id);
                }}
              >
                <span className={s.currentTitle}>{row.task.title || "Untitled task"}</span>
                <span
                  className={s.currentList}
                  style={{ color: row.list.colorKey ? `var(--tl-${row.list.colorKey})` : "var(--accent)" }}
                >
                  {row.list.title}
                </span>
                {row.task.dueDate ? (
                  <span className={s.currentDue}>{dueLabel(row.task.dueDate, row.task.dueTime)}</span>
                ) : null}
                <span className={s.currentSeq}>{row.task.seq !== undefined ? `#${row.task.seq}` : "#···"}</span>
              </button>
            </div>
          ))}
        </div>
      )}
      {open.length > shown ? (
        <Button variant="ghost" size="sm" onClick={() => setShown((n) => n + CURRENT_PAGE)}>
          Show more ({open.length - shown} left)
        </Button>
      ) : null}
    </section>
  );
}

function StatTile({ label, value }: { label: string; value: number | undefined }) {
  return (
    <div className={s.tile}>
      <span className={s.tileValue}>{value === undefined ? "–" : compact(value)}</span>
      <span className={s.tileLabel}>{label}</span>
    </div>
  );
}
